import { ref, computed } from '@vue/composition-api'

/**
 * 集計データをキャストグループ別に合算するcomposable
 *
 * ReadOnlyAggregationTableのcontexts形式を入出力として使い、
 * グループヘッダー行（合算値）+ メンバー行の新しいcontexts配列を生成する。
 *
 * @param {Object} options
 * @param {Ref<Array>} options.contexts - ReadOnlyAggregationTable形式のcontexts配列
 * @param {Ref<Array>} options.groupMapping - ユーザーIDとグループの対応
 *   [{ userId: number, castGroup: { id, name } | null }]
 *
 * @example
 * const { groupedContexts, isGrouped, toggleGrouped } = useGroupedAggregation({
 *   contexts: computed(() => aggregation.attributes.contexts.data),
 *   groupMapping: computed(() => apiResult.map(u => ({
 *     userId: u.user_id,
 *     castGroup: u.cast_group,
 *   }))),
 * })
 *
 * // ReadOnlyAggregationTableに渡す
 * const displayContexts = computed(() => isGrouped.value ? groupedContexts.value : contexts.value)
 */
export default ({ contexts, groupMapping }) => {
  const isGrouped = ref(false)

  const toggleGrouped = () => {
    isGrouped.value = !isGrouped.value
  }

  /**
   * 複数ユーザーのresultsを列ごとに合算する
   */
  const sumResults = members => {
    if (members.length === 0) return []

    const firstResults = members[0]?.attributes?.results?.data || []

    return firstResults.map((firstResult, idx) => {
      let sum = 0
      let hasValue = false

      members.forEach(member => {
        const memberResult = member.attributes?.results?.data?.[idx]
        const val = memberResult?.attributes?.value
        if (val != null && val !== '' && !Number.isNaN(Number(val))) {
          sum += Number(val)
          hasValue = true
        }
      })

      return {
        attributes: {
          ...firstResult.attributes,
          businessRuleResultId: hasValue ? -1 : null,
          value: hasValue ? String(sum) : null,
          editable: false,
        },
      }
    })
  }

  const groupedContexts = computed(() => {
    if (!contexts.value || !groupMapping.value) return contexts.value || []

    // ユーザーIDからグループ情報へのマップを作成
    const userGroupMap = {}
    groupMapping.value.forEach(({ userId, castGroup }) => {
      userGroupMap[userId] = castGroup
    })

    // contextsをグループIDでグルーピング
    const groups = {}
    const ungrouped = []

    contexts.value.forEach(ctx => {
      const userId = ctx.attributes?.id
      const group = userGroupMap[userId]

      if (group && group.id) {
        if (!groups[group.id]) {
          groups[group.id] = { id: group.id, name: group.name, members: [] }
        }
        groups[group.id].members.push(ctx)
      } else {
        ungrouped.push(ctx)
      }
    })

    // グループ別にソートして結果を構築
    const result = []
    const sortedGroups = Object.values(groups).sort((a, b) => a.id - b.id)

    sortedGroups.forEach(group => {
      // グループ行（合算値のみ）
      const summedResults = sumResults(group.members)
      result.push({
        attributes: {
          contextType: 'User',
          id: `group-${group.id}`,
          name: group.name,
          results: { data: summedResults },
        },
      })
    })

    // 未所属ユーザーはグループなしで個別表示
    ungrouped.forEach(ctx => {
      result.push(ctx)
    })

    return result
  })

  return {
    groupedContexts,
    isGrouped,
    toggleGrouped,
  }
}
